/**
 * SettingsBrandedEmails — Iter 191k.
 *
 * Custom-domain email delivery via Resend. Vendor pastes their sending
 * domain, we register it with Resend, and show DKIM/SPF DNS records they
 * need to add. Once verified, transactional emails (invoices, orders,
 * proposals, contracts) send from their brand instead of EntrepreNatives.
 *
 * Moved from the old combined "Payments & Branded Emails" page so this now
 * lives under Domains & Email where it conceptually belongs.
 *
 * Endpoints (unchanged from previous page):
 *   GET    /api/builder/email/domains
 *   POST   /api/builder/email/domains       {name}
 *   POST   /api/builder/email/domains/{id}/verify
 *   DELETE /api/builder/email/domains/{id}
 *   GET    /api/builder/email/sender
 *   PUT    /api/builder/email/sender
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Loader2, Copy, Trash2, Mail, ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardBackdrop, GlassCard } from '@/components/dashboard/dashboard-ui';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <div className="text-xs text-white/50 mb-1.5">{label}</div>
    <input
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 outline-none focus:border-violet-400 text-sm"
    />
  </div>
);

function EmailDomainCard() {
  const [domains, setDomains] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [verifying, setVerifying] = useState({});
  const [sender, setSender] = useState({ from_name: '', reply_to: '', default_domain_id: '', default_local_part: 'hello' });

  const load = async () => {
    try {
      const [{ data: doms }, { data: snd }] = await Promise.all([
        axios.get(`${API}/builder/email/domains`, { headers: authHeaders() }),
        axios.get(`${API}/builder/email/sender`,  { headers: authHeaders() }),
      ]);
      setDomains(doms || []);
      setSender(prev => ({ ...prev, ...(snd?.email_sender || {}) }));
    } catch { /* unauthenticated edge */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/builder/email/domains`, { name: name.trim().toLowerCase() }, { headers: authHeaders() });
      setDomains(prev => [data, ...prev.filter(d => d.id !== data.id)]);
      setName('');
      toast.success('Domain registered — add the DNS records below.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not register domain'); }
    finally { setCreating(false); }
  };
  const verify = async (id) => {
    setVerifying(v => ({ ...v, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/builder/email/domains/${id}/verify`, {}, { headers: authHeaders() });
      setDomains(prev => prev.map(d => d.id === id ? data : d));
      toast[data.status === 'verified' ? 'success' : 'message'](data.status === 'verified' ? 'Domain verified — emails will now send from your brand.' : `Status: ${data.status}. DNS may take a few minutes to propagate.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not verify'); }
    finally { setVerifying(v => ({ ...v, [id]: false })); }
  };
  const remove = async (id) => {
    if (!(await themedConfirm('Remove this domain? Emails will fall back to the platform sender.'))) return;
    try { await axios.delete(`${API}/builder/email/domains/${id}`, { headers: authHeaders() }); setDomains(prev => prev.filter(d => d.id !== id)); }
    catch { toast.error('Could not remove'); }
  };
  const saveSender = async () => {
    try {
      const { data } = await axios.put(`${API}/builder/email/sender`, sender, { headers: authHeaders() });
      setSender(prev => ({ ...prev, ...(data.email_sender || {}) }));
      toast.success('Sender settings saved.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success('Copied'); };

  return (
    <GlassCard className="p-6" data-testid="email-domain-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/20 border border-white/10 flex items-center justify-center">
          <Mail className="w-6 h-6 text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white">Branded Emails (Custom Domain)</h3>
          <p className="text-sm text-white/60 mt-1">
            Send invoices, proposals, and contracts from <span className="text-white font-medium">your own domain</span> (e.g.
            <code className="mx-1 px-1 py-0.5 rounded bg-white/10 text-cyan-200">hello@yourbusiness.com</code>).
          </p>

          <div className="mt-5 flex gap-2">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="yourbusiness.com"
              data-testid="email-domain-input"
              className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-violet-400 outline-none text-sm"
            />
            <button
              onClick={create}
              disabled={creating || !name.trim()}
              data-testid="email-domain-create"
              className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Add domain
            </button>
          </div>

          {domains.length > 0 && (
            <div className="mt-6 space-y-4">
              {domains.map(d => (
                <div key={d.id} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`email-domain-row-${d.id}`}>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{d.name}</div>
                      <div className="text-xs text-white/40 mt-0.5">
                        Status:&nbsp;
                        <span className={d.status === 'verified' ? 'text-emerald-300' : d.status === 'failed' ? 'text-rose-300' : 'text-amber-300'}>{d.status}</span>
                        {d.verified_at && <> · verified {new Date(d.verified_at).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => verify(d.id)} disabled={verifying[d.id]} data-testid={`email-domain-verify-${d.id}`} className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs flex items-center gap-1.5">
                        {verifying[d.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Verify
                      </button>
                      <button onClick={() => remove(d.id)} data-testid={`email-domain-remove-${d.id}`} className="px-2 py-1.5 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {d.status !== 'verified' && d.records?.length > 0 && (
                    <div className="px-4 pb-4 pt-2 border-t border-white/5">
                      <div className="text-xs text-white/50 mb-2">Add these DNS records at your domain registrar, then click <b>Verify</b>.</div>
                      <div className="rounded-lg border border-white/10 overflow-hidden text-xs">
                        <div className="grid grid-cols-12 px-3 py-2 bg-white/5 text-white/40 uppercase tracking-wide">
                          <div className="col-span-1">Type</div>
                          <div className="col-span-4">Name</div>
                          <div className="col-span-6">Value</div>
                          <div className="col-span-1"></div>
                        </div>
                        {d.records.map((r, i) => (
                          <div key={i} className="grid grid-cols-12 px-3 py-2 border-t border-white/5 items-center">
                            <div className="col-span-1 text-cyan-300 font-mono">{r.type || r.record}</div>
                            <div className="col-span-4 text-white/80 font-mono break-all">{r.name}</div>
                            <div className="col-span-6 text-white/60 font-mono break-all">{r.value}</div>
                            <button className="col-span-1 text-white/40 hover:text-white" onClick={() => copy(r.value)} title="Copy value">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Sender settings */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <h4 className="text-sm font-semibold text-white mb-1">Sender details</h4>
            <p className="text-xs text-white/50 mb-4">How the From-name and reply-to address appear on outgoing email.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="From name (your business name)" value={sender.from_name} onChange={v => setSender(s => ({ ...s, from_name: v }))} testid="sender-from-name" placeholder="Sarah's Studio" />
              <Field label="Reply-to email (optional)" value={sender.reply_to} onChange={v => setSender(s => ({ ...s, reply_to: v }))} testid="sender-reply-to" placeholder="sarah@yourbusiness.com" />
              <div>
                <div className="text-xs text-white/50 mb-1.5">Send-from domain</div>
                <select
                  value={sender.default_domain_id || ''}
                  onChange={e => setSender(s => ({ ...s, default_domain_id: e.target.value }))}
                  data-testid="sender-default-domain"
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400"
                >
                  <option value="">Use EntrepreNatives default sender</option>
                  {domains.filter(d => d.status === 'verified').map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <Field label="Local part (e.g. hello)" value={sender.default_local_part} onChange={v => setSender(s => ({ ...s, default_local_part: v }))} testid="sender-local-part" placeholder="hello" />
            </div>
            <button onClick={saveSender} data-testid="sender-save" className="mt-4 px-5 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold">
              Save sender details
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

export default function SettingsBrandedEmails() {
  return (
    <div className="relative min-h-full">
      <DashboardBackdrop />
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="settings-branded-emails">
        <Link to="/website-builder/settings" data-testid="settings-go-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] transition-colors font-semibold">
          <ArrowLeft className="w-3 h-3" /> Go Back
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Branded Emails</h1>
          <p className="text-white/60 mt-2">
            Verify your custom domain so invoices, order confirmations, and contracts arrive from
            {' '}<span className="text-white">your brand</span> — not the EntrepreNatives platform.
          </p>
        </div>
        <EmailDomainCard />
      </div>
    </div>
  );
}
